/**
 * HEnemy — horizontal-mode enemy.
 * Enters from the right (x = GAME.width+32) and moves left.
 * hp = 1 (basic/fast) or 3 (tank), dies on first hit or when offscreen left.
 *
 * ENEMY-BULLETS (task #2, 2026-05-21): enemies now periodically fire a small
 * red bullet leftward at the player. Each variant has its own fire interval:
 *   basic: 2000ms, fast: 1000ms, tank: 4000ms.
 * Production rendering / collision lives in HorizontalBattleScene; this class
 * exposes a pure `tryFire(nowMs)` that returns an `EnemyBulletSpec` when the
 * cooldown has elapsed, otherwise null. Keeps HEnemy Phaser-free for tests.
 *
 * HENEMY-AFFINITY (2026-05-21): horizontal-mode enemies now carry an optional
 * `affinity: FactionId | null` field that mirrors the top-down `Enemy` class.
 * When non-null, the scene draws a faint coloured halo behind the sprite and
 * the player skill matching the affinity does 1.5× damage. Default null = no
 * halo (basic-grunt rendering preserved). This is what the boss affinity
 * tutorial promises — without it kids see no weakness cues on grunts and get
 * confused (UX feedback, docs/UX_FEEDBACK_KID.md 2026-05-21 01:11).
 */
import type { FactionId } from '../../shared/types';
import { AFFINITY_HALO_COLORS } from './enemy';
import { followPathStep } from '../ai/pathfinder';

export type HEnemyVariant = 'basic' | 'fast' | 'tank';

/**
 * enemy-diversity (2026-05-31, boss 回饋「不只單一直線、有些特別快、成群結隊」):
 * horizontal-mode enemies now support 4 movement patterns instead of a single
 * left-ward straight line. All patterns still drift LEFT (vx<0) so the wave
 * always sweeps toward the player; the difference is the vertical behaviour.
 *   - 'straight': legacy constant (vx,vy). DEFAULT — keeps every old test green.
 *   - 'sine':     bobs up/down around its spawn Y (adds a wavy column).
 *   - 'diagonal': constant vy (set by ctor) so it slides across at an angle.
 *   - 'chaseY':   eases its Y toward the player's Y each frame (soft homing on
 *                 the vertical axis only — never reverses X, so a K-3 kid can
 *                 always out-run it forward).
 */
export type HEnemyMovePattern = 'straight' | 'sine' | 'diagonal' | 'chaseY' | 'chase';

/** Vertical speed (px/sec) a 'chaseY' enemy uses to ease toward the player Y. */
export const CHASE_Y_SPEED_PX_PER_SEC = 90;
/**
 * 'chase' 真追逐速度(px/sec):X+Y 都朝玩家逼近,會貼上來 → 近戰武器才有用
 * (老闆 2026-06-06「可以有追逐主角的敵人,這樣近戰才有用」)。刻意 < 玩家移速,
 * K-3 仍能拉開距離前逃,但不處理就會被貼身。
 */
export const CHASE_SPEED_PX_PER_SEC = 115;
/** Default sine bob amplitude (px) and period (sec) for 'sine' enemies. */
export const SINE_DEFAULT_AMP_PX = 60;
export const SINE_DEFAULT_PERIOD_SEC = 1.6;

/** Default fire intervals (ms) per variant. */
export const DEFAULT_FIRE_INTERVAL_MS: Record<HEnemyVariant, number> = {
  basic: 1300,
  fast: 650,
  tank: 2600,
};

/**
 * enemy-toughness (2026-05-31, boss 回饋「敵人太弱,一打就死」): per-variant
 * default HP so grunts survive ≥2 hits and feel like they take damage instead
 * of popping on the first bullet.
 *   - basic 1→2: a basic now needs 2 shots (was a one-tap).
 *   - fast  1→2: stays fragile-ish but no longer a one-tap; still the 快脆 type.
 *   - tank  3→6: the 耐打 type doubles so it visibly soaks fire.
 * K-3 仍打得贏:玩家基礎子彈傷害 1/發、攻速快、有三選一強化,雜兵 2-6 HP
 * 仍是「幾發就清」的節奏,只是不再一槍死、有打擊感。
 * Used by the constructor when `options.hp` is not explicitly provided; tests
 * and the mini-boss can still override via the ctor option.
 */
export const DEFAULT_HP: Record<HEnemyVariant, number> = {
  basic: 3,
  fast: 3,
  tank: 12,
};

/** Spec returned by tryFire — caller spawns a bullet with these fields. */
export interface EnemyBulletSpec {
  x: number;
  y: number;
  vx: number;
  vy: number;
  damage: number;
}

/** Bullet velocity in px/sec (leftward). */
export const ENEMY_BULLET_VX = -300;
/** Bullet damage on player hit. */
export const ENEMY_BULLET_DAMAGE = 5;

/** Damage multiplier when the player's skill faction matches enemy affinity. */
export const HENEMY_AFFINITY_MULTIPLIER = 1.5;

export class HEnemy {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /**
   * Current hit points. Defaults to DEFAULT_HP[variant] (basic/fast 2, tank 6)
   * unless `options.hp` overrides. enemy-toughness 2026-05-31: was a flat 1.
   */
  hp: number = DEFAULT_HP.basic;
  /** Max HP captured at spawn — drives the受擊閃白 / mini HP-bar fill ratio. */
  maxHp: number = DEFAULT_HP.basic;
  alive: boolean = true;
  readonly displaySize: number = 32;

  /** Variant id, used to pick fire interval + cosmetic sprite. */
  readonly variant: HEnemyVariant;
  /** ms between shots for this enemy. Per-variant default; can be overridden. */
  fireIntervalMs: number;
  /** Last time tryFire emitted a bullet (ms). -1 = never fired yet (sentinel). */
  lastFireMs: number = -1;
  /**
   * Faction this enemy is weak to. null = no halo / no affinity multiplier
   * (legacy basic-grunt behaviour). When non-null, the scene draws a halo and
   * matching skills deal HENEMY_AFFINITY_MULTIPLIER × base damage.
   */
  affinity: FactionId | null;
  /**
   * G06 pathfinding (opt-in). When non-null, `update()` follows this list of
   * world-pixel waypoints instead of moving at constant `(vx, vy)`. The owning
   * scene is responsible for refreshing `path` ~every 0.5s via a Pathfinder
   * instance. Default null = legacy straight-line behaviour (1251 existing
   * tests unchanged).
   */
  path: Array<{ x: number; y: number }> | null = null;
  /** Speed used when following `path` (px/sec). Defaults to |vx|. */
  chaseSpeedPxPerSec: number;

  // ── enemy-diversity: movement pattern state ──────────────────────────────
  /** Active movement pattern. Default 'straight' = legacy behaviour. */
  movePattern: HEnemyMovePattern = 'straight';
  /** Baseline Y captured at spawn — the centre that 'sine' bobs around. */
  readonly baseY: number;
  /** 'sine' bob amplitude (px). */
  sineAmpPx: number = SINE_DEFAULT_AMP_PX;
  /** 'sine' bob period (sec). */
  sinePeriodSec: number = SINE_DEFAULT_PERIOD_SEC;
  /** Seconds elapsed since spawn — drives the sine phase. */
  elapsedSec: number = 0;
  /**
   * For 'chaseY': the player's current Y. The owning scene refreshes this each
   * frame BEFORE calling update() (via the _updateEnemies loop). When null the
   * enemy falls back to straight vertical drift so a mis-wired scene never NaNs.
   */
  chaseTargetY: number | null = null;
  /**
   * For 'chase'(真追逐):the player's current X. Scene refreshes each frame
   * before update() (同 chaseTargetY)。null → fallback 直線左移,不 NaN。
   */
  chaseTargetX: number | null = null;

  constructor(
    position: { x: number; y: number },
    velocity: { vx: number; vy: number } = { vx: -150, vy: 0 },
    options: {
      variant?: HEnemyVariant;
      fireIntervalMs?: number;
      affinity?: FactionId | null;
      movePattern?: HEnemyMovePattern;
      sineAmpPx?: number;
      sinePeriodSec?: number;
      /** enemy-toughness: explicit HP override; defaults to DEFAULT_HP[variant]. */
      hp?: number;
    } = {},
  ) {
    this.x = position.x;
    this.y = position.y;
    this.vx = velocity.vx;
    this.vy = velocity.vy;
    this.variant = options.variant ?? 'basic';
    this.fireIntervalMs = options.fireIntervalMs ?? DEFAULT_FIRE_INTERVAL_MS[this.variant];
    // enemy-toughness: per-variant default HP (basic/fast 2, tank 6) unless the
    // caller passes an explicit hp. maxHp mirrors the starting hp so the scene
    // can render a受擊閃白 / fill-ratio cue.
    this.hp = options.hp ?? DEFAULT_HP[this.variant];
    this.maxHp = this.hp;
    this.affinity = options.affinity ?? null;
    // enemy-diversity: capture baseline Y + optional pattern/amp/period.
    this.baseY = position.y;
    this.movePattern = options.movePattern ?? 'straight';
    if (options.sineAmpPx != null) this.sineAmpPx = options.sineAmpPx;
    if (options.sinePeriodSec != null) this.sinePeriodSec = options.sinePeriodSec;
    // Default chase speed: magnitude of horizontal entry velocity, so opting
    // in to pathfinding mid-flight doesn't make the grunt suddenly sprint.
    this.chaseSpeedPxPerSec = Math.abs(this.vx) || 150;
  }

  update(dtSec: number): void {
    if (!this.alive) return;
    if (this.path && this.path.length > 0) {
      // G06: path-follow mode. Step toward next waypoint; drop reached nodes.
      const next = followPathStep(
        { x: this.x, y: this.y },
        this.path,
        this.chaseSpeedPxPerSec,
        dtSec,
      );
      this.x = next.x;
      this.y = next.y;
      this.path = next.remaining;
    } else {
      // enemy-diversity: X always advances by vx (every pattern sweeps left).
      this.x += this.vx * dtSec;
      this.elapsedSec += dtSec;
      switch (this.movePattern) {
        case 'sine':
          // Bob around the captured baseline so the column reads as a wave.
          this.y = this.baseY + this.sineAmpPx * Math.sin(
            (this.elapsedSec / this.sinePeriodSec) * Math.PI * 2,
          );
          break;
        case 'chaseY': {
          // Ease Y toward the player (vertical-only soft homing). Clamp the
          // per-frame step to CHASE_Y_SPEED so it can't teleport.
          if (this.chaseTargetY != null) {
            const dy = this.chaseTargetY - this.y;
            const maxStep = CHASE_Y_SPEED_PX_PER_SEC * dtSec;
            this.y += Math.max(-maxStep, Math.min(maxStep, dy));
          } else {
            this.y += this.vy * dtSec;
          }
          break;
        }
        case 'chase': {
          // 真追逐:X+Y 都朝玩家逼近(會貼上來 → 近戰武器才有用)。先抵銷上方
          // 預設的 vx 左移,再以 CHASE_SPEED 朝玩家移。速度 < 玩家移速,可前逃。
          if (this.chaseTargetX != null && this.chaseTargetY != null) {
            this.x -= this.vx * dtSec; // 取消預設左移,改由下方朝玩家補正
            const dx = this.chaseTargetX - this.x;
            const dy = this.chaseTargetY - this.y;
            const len = Math.hypot(dx, dy) || 1;
            const step = CHASE_SPEED_PX_PER_SEC * dtSec;
            this.x += (dx / len) * step;
            this.y += (dy / len) * step;
          } else {
            this.y += this.vy * dtSec;
          }
          break;
        }
        case 'diagonal':
        case 'straight':
        default:
          // diagonal uses a non-zero vy supplied at spawn; straight uses vy (0).
          this.y += this.vy * dtSec;
          break;
      }
    }
    // Remove when it exits the left side
    if (this.x < -this.displaySize) {
      this.alive = false;
    }
  }

  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - amount);
    if (this.hp <= 0) this.alive = false;
  }

  /**
   * Returns the halo color (0xRRGGBB) for this enemy's affinity, or null when
   * the enemy has no affinity set. Mirrors the top-down `Enemy.haloColor()`
   * but null-safe for legacy grunts.
   */
  haloColor(): number | null {
    return this.affinity ? AFFINITY_HALO_COLORS[this.affinity] : null;
  }

  /**
   * Returns damage after applying affinity multiplier.
   * - Enemy has no affinity → returns baseDamage unchanged.
   * - skillFaction null OR mismatch → returns baseDamage unchanged.
   * - skillFaction === affinity → returns baseDamage * HENEMY_AFFINITY_MULTIPLIER.
   *
   * Mirrors `applyAffinityMultiplier` in src/game/systems/combat.ts but
   * scoped to HEnemy's optional-affinity model. Keep HEnemy Phaser-free.
   */
  effectiveDamageFor(skillFaction: FactionId | null, baseDamage: number): number {
    if (this.affinity && skillFaction === this.affinity) {
      return baseDamage * HENEMY_AFFINITY_MULTIPLIER;
    }
    return baseDamage;
  }

  /**
   * Draws a faint faction-coloured halo behind the enemy sprite using a Phaser
   * Graphics object. cx/cy are screen-space centre coords (caller passes the
   * enemy's render position). No-op if `affinity` is null. Cheap — single
   * fillCircle, no particles / no animation. Slight alpha pulse driven by
   * `nowMs` (0.25 → 0.45 over ~1.5s) so the halo doesn't read as static UI.
   */
  drawHalo(
    graphics: {
      fillStyle(color: number, alpha: number): void;
      fillCircle(x: number, y: number, r: number): void;
    },
    cx: number,
    cy: number,
    nowMs: number = 0,
    radius?: number,
  ): void {
    const color = this.haloColor();
    if (color === null) return;
    const r = radius ?? this.displaySize * 0.7;
    // Pulse alpha 0.25 → 0.45 over a 1.5s cycle (matches Boss.drawAffinityHalo
    // amplitude, slightly lower base so the grunt halo doesn't outshine the
    // boss halo which is the "main event" affinity cue).
    const pulse = 0.35 + 0.1 * Math.sin((nowMs / 1500) * Math.PI * 2);
    graphics.fillStyle(color, pulse);
    graphics.fillCircle(cx, cy, r);
  }

  /**
   * If enough time has elapsed since the last shot, advance the cooldown and
   * return a bullet spec. Otherwise return null.
   *
   * First call after spawn (lastFireMs === 0): seeds lastFireMs to `nowMs` so
   * the first shot fires one full interval later (no "shoot immediately on
   * entry" surprise — gives the player a beat to react).
   *
   * Dead enemies never fire.
   */
  tryFire(nowMs: number): EnemyBulletSpec | null {
    if (!this.alive) return null;
    if (this.lastFireMs < 0) {
      // First call after spawn: seed cooldown anchor at the *current* tick so
      // the first actual shot fires `fireIntervalMs` later. Using -1 sentinel
      // (rather than 0) lets us seed correctly even when nowMs === 0.
      this.lastFireMs = nowMs;
      return null;
    }
    if (nowMs - this.lastFireMs < this.fireIntervalMs) return null;
    this.lastFireMs = nowMs;
    return {
      x: this.x - this.displaySize / 2,
      y: this.y,
      vx: ENEMY_BULLET_VX,
      vy: 0,
      damage: ENEMY_BULLET_DAMAGE,
    };
  }
}
